// The product page's reads and writes (apps/server/src/productPage.mjs).
import { authenticatedFetch } from '../auth/authClient';

export interface CatalogProduct { id: string; code: string; name: string; structure: string; status: string; is_component: boolean }
export interface CatalogGroup {
  id: string;
  code: string;
  name: string;
  family: string;
  revenue_stream: string;
  required: boolean;
  max_per_quote: number | null;
  products: CatalogProduct[];
}

export interface ProductOption {
  id: string;
  product: { id: string; code: string; name: string; structure: string; status: string };
  option_group: string | null;
  required: boolean;
  included: boolean;
  min_quantity: number | null;
  max_quantity: number | null;
  default_quantity: number | null;
  sort_order: number;
  estimated_hours: number | null;
  default_price: null | {
    id: string;
    label: string | null;
    scheme: string;
    amount: number;
    period: string | null;
    selling_model: string | null;
    price_grid: string | null;
  };
  children: ProductOption[];
}

export interface Treatment {
  id: string;
  name: string;
  method: string;
  billing_timing: string | null;
  billing_frequency: string;
  is_default: boolean;
  sort_order: number;
  items: { sequence: number; label: string; percent: number; trigger: string; month_offset: number | null }[];
  quote_lines: number;
  order_lines: number;
}

export interface ProductPageData {
  product: { id: string; code: string; name: string; structure: 'single' | 'bundle'; status: string; is_component: boolean; group_id: string };
  catalog: { family: string; groups: CatalogGroup[] }[];
  options: ProductOption[];
  partOf: { option_id: string; id: string; code: string; name: string; status: string; option_group: string | null; required: boolean; included: boolean }[];
  billing: { policy: { id: string; name: string } | null; treatments: Treatment[] };
  roleSplits: { id: string; resource_role_id: string; name: string; share_pct: number }[];
  pricing: {
    default_price: null | {
      id: string;
      label: string | null;
      scheme: 'flat' | 'per_hour' | 'grid' | 'included';
      amount: number;
      period: 'month' | 'year' | null;
      effective_from: string;
      selling_model: { id: number; name: string; kind: string };
      grid: null | { id: string; name: string; prices_by: string | null; dimensions: string[]; min_amount: number | null; max_amount: number | null };
    };
    other_prices: number;
  };
  sales: {
    scheduled: { month: string; one_time: number; recurring: number }[];
    by_company_size: { key: string | null; label: string | null; one_time: number; recurring: number; lines: number }[];
    open_lines: {
      id: string;
      quote: { id: string; name: string };
      account: { id: string; name: string };
      opportunity: { id: string; name: string } | null;
      stage: string | null;
      company_size: string | null;
      billing: 'one_time' | 'monthly';
      quantity: number;
      total: number | null;
    }[];
    open_lines_truncated: boolean;
  };
}

async function send<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authenticatedFetch(path, init);
  const body = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok || !body) throw new Error(body?.error ?? `The request failed (${res.status}).`);
  return body;
}

const post = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export const loadProductPage = (id: string) => send<ProductPageData>(`/api/products/${encodeURIComponent(id)}/page`);

/** Moves products into a group, before one of its products or last; renumbers every group it touches. */
export const moveProducts = (productIds: string[], groupId: string, beforeId: string | null) =>
  send<{ groups: { id: string; productIds: string[] }[] }>('/api/products/move', post({ productIds, groupId, beforeId }));

/** A bundle's own options in their new order, each with its option group. */
export const orderOptions = (bundleId: string, options: { id: string; option_group: string | null }[]) =>
  send<{ options: { id: string; option_group: string | null; sort_order: number }[] }>(
    `/api/products/${encodeURIComponent(bundleId)}/options/order`,
    post({ options }),
  );

/** A bundle option's own fields (the product-options object). */
export async function saveOption(id: string | null, fields: Record<string, unknown>): Promise<void> {
  const path = id ? `/api/objects/product-options/${encodeURIComponent(id)}` : '/api/objects/product-options';
  const res = await authenticatedFetch(path, { method: id ? 'PATCH' : 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(fields) });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `The option could not be saved (${res.status}).`);
  }
}

export async function removeOption(id: string): Promise<void> {
  const res = await authenticatedFetch(`/api/objects/product-options/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error(`The option could not be removed (${res.status}).`);
}
